import { useMemo } from "react";

export type PatioStatus = "available" | "charging" | "alert" | "offline";

export interface PatioMatrixProps {
  layout: Array<{ cpId: string; slot: string; status: PatioStatus }>;
  onSelect?: (cpId: string) => void;
}

const STATUS_LABEL: Record<PatioStatus, string> = {
  available: "Livre",
  charging: "Carregando",
  alert: "Alerta",
  offline: "Sem sinal",
};

const STATUS_ORDER: PatioStatus[] = ["charging", "available", "alert", "offline"];

function slotNumber(slot: string): number {
  const value = Number.parseInt(slot.replace(/\D/g, ""), 10);
  return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
}

/** Carregador rápido em perspectiva para representar cada posição do pátio. */
function PatioChargerIcon() {
  return (
    <svg className="nx-patio-charger__icon" viewBox="0 0 52 58" aria-hidden="true">
      <ellipse className="nx-patio-charger__shadow" cx="24" cy="51" rx="17" ry="3.5" />
      <path className="nx-patio-charger__base" d="M10 47h28l4 5H7l3-5Z" />
      <path className="nx-patio-charger__side" d="M34 8 40 13v34l-6 3V8Z" />
      <rect className="nx-patio-charger__body" x="12" y="8" width="23" height="42" rx="4" />
      <rect className="nx-patio-charger__screen" x="16" y="13" width="15" height="12" rx="1.5" />
      <path className="nx-patio-charger__screen-line" d="M19 17h9M19 20h6" />
      <path className="nx-patio-charger__bolt" d="m26 29-5 8h4l-2 7 7-10h-4l2-5Z" />
      <path className="nx-patio-charger__cable" d="M36 29c8 0 9 6 6 11-1 2-3 3-5 3" />
      <path className="nx-patio-charger__plug" d="M35 43v4m3-4v4m-4 0h5v3h-5z" />
    </svg>
  );
}

export function PatioMatrix({ layout, onSelect }: PatioMatrixProps) {
  const sorted = useMemo(() => [...layout].sort((a, b) => {
    const slotDelta = slotNumber(a.slot) - slotNumber(b.slot);
    return slotDelta || a.cpId.localeCompare(b.cpId, "pt-BR", { numeric: true });
  }), [layout]);

  const counts = useMemo(() => {
    const result: Record<PatioStatus, number> = { available: 0, charging: 0, alert: 0, offline: 0 };
    for (const item of layout) result[item.status] += 1;
    return result;
  }, [layout]);

  const slots = useMemo(() => {
    const groups = new Map<string, typeof sorted>();
    for (const item of sorted) {
      const group = groups.get(item.slot) ?? [];
      group.push(item);
      groups.set(item.slot, group);
    }
    return [...groups.entries()];
  }, [sorted]);

  return (
    <div className="nx-patio" aria-label={`Pátio com ${layout.length} carregadores`}>
      <div className="nx-patio__legend" aria-label="Legenda do pátio">
        {STATUS_ORDER.map((status) => <span key={status} className={`nx-patio__legend-item nx-patio__legend-item--${status}`}><i aria-hidden="true" />{STATUS_LABEL[status]} <b>{counts[status]}</b></span>)}
      </div>
      <div className="nx-patio__ground">
        <div className="nx-patio__entry" aria-hidden="true"><span>Entrada</span><i /></div>
        <div className="nx-patio__lanes" role="grid" aria-rowcount={slots.length} aria-colcount={Math.max(...slots.map(([, items]) => items.length), 0)}>
          {slots.map(([slot, items]) => <div className="nx-patio__lane" role="row" key={slot}>
            <span className="nx-patio__lane-label" aria-hidden="true">{slot}</span>
            <div className="nx-patio__bays">
              {items.map((charger) => <PatioBay key={`${charger.cpId}-${charger.slot}`} charger={charger} onSelect={onSelect} />)}
            </div>
          </div>)}
        </div>
      </div>
      <p className="nx-patio__caption">{layout.length} {layout.length === 1 ? "carregador" : "carregadores"} no pátio</p>
    </div>
  );
}

function PatioBay({ charger, onSelect }: { charger: PatioMatrixProps["layout"][number]; onSelect?: ((cpId: string) => void) | undefined }) {
  const label = `${charger.cpId}, vaga ${charger.slot}: ${STATUS_LABEL[charger.status]}`;
  const content = <><PatioChargerIcon /><span className="nx-patio-charger__status">{STATUS_LABEL[charger.status]}</span><span className="nx-patio-charger__id mono">{charger.cpId}</span></>;
  const className = `nx-patio-charger nx-patio-charger--${charger.status}`;
  if (onSelect) return <button className={className} type="button" onClick={() => onSelect(charger.cpId)} aria-label={label} title={label}>{content}</button>;
  return <div className={className} role="gridcell" tabIndex={0} aria-label={label} title={label}>{content}</div>;
}

export default PatioMatrix;
